import React, { useState } from "react";
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import { updateSFQStatus, fetchSFQDetail } from "../Redux/feasibility";
import { STATUS_LABELS, STATUS_COLOR, findSubmitBlockers } from "./data";

const Wrap = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  margin-bottom: 14px;
  flex-wrap: wrap;
`;

const StatusChip = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 600;
  background: ${(p) => (p.$color || "#6c757d") + "22"};
  color: ${(p) => p.$color || "#6c757d"};
  border: 1px solid ${(p) => p.$color || "#6c757d"};
`;

const VersionChip = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 600;
  background: #e7f1ff;
  color: #0d6efd;
  border: 1px solid #0d6efd;
`;

const DirtyNote = styled.span`
  font-size: 12px;
  color: #7a5c00;
`;

// Why Submit for Review is greyed out. Sits inline next to the button, like DirtyNote.
const BlockedNote = styled.span`
  font-size: 12px;
  color: #842029;
`;

const Divider = styled.span`
  width: 1px;
  align-self: stretch;
  background: #dee2e6;
`;

const ActionBtn = styled.button`
  padding: 5px 14px;
  border-radius: 4px;
  border: 1px solid ${(p) => p.$color || "#0d6efd"};
  background: ${(p) => (p.$filled ? (p.$color || "#0d6efd") : "#fff")};
  color: ${(p) => (p.$filled ? "#fff" : (p.$color || "#0d6efd"))};
  font-size: 12px;
  cursor: pointer;
  &:disabled { opacity: 0.4; cursor: not-allowed; }
  &:hover:not(:disabled) { filter: brightness(0.92); }
`;

// Reason input, shared by Send Back and rollback: full-width row below the buttons,
// since Wrap wraps. $color tints it to match whichever action opened it.
const ReasonBox = styled.div`
  flex-basis: 100%;
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px;
  border: 1px solid ${(p) => p.$color || "#dc3545"};
  border-radius: 4px;
  background: #fff;
`;

const ReasonLabel = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: ${(p) => p.$color || "#dc3545"};
`;

const ReasonInput = styled.textarea`
  width: 100%;
  min-height: 64px;
  resize: vertical;
  padding: 6px 8px;
  font-size: 12px;
  font-family: inherit;
  border: 1px solid #dee2e6;
  border-radius: 4px;
`;

const ReasonFoot = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CharCount = styled.span`
  margin-left: auto;
  font-size: 11px;
  color: ${(p) => (p.$over ? "#dc3545" : "#adb5bd")};
`;

// Shown to the CRA after a Send Back, until the study leaves Draft again.
const SendBackNotice = styled.div`
  flex-basis: 100%;
  padding: 8px 10px;
  border-left: 3px solid #dc3545;
  border-radius: 3px;
  background: #fff5f5;
  font-size: 12px;
  color: #842029;
  white-space: pre-wrap;
`;

// The rollback counterpart, shown while the study sits back in InternalReview: the
// reviewer finds a decision they already made waiting for them again, and this is where
// they read why. Tinted to InternalReview's own colour rather than the rejection red —
// a rollback is not a rejection.
const RollbackNotice = styled.div`
  flex-basis: 100%;
  padding: 8px 10px;
  border-left: 3px solid #fd7e14;
  border-radius: 3px;
  background: #fff8f0;
  font-size: 12px;
  color: #7a4100;
  white-space: pre-wrap;
`;

const NoticeTitle = styled.strong`
  display: block;
  margin-bottom: 3px;
`;

const REASON_MAX = 1000;

// Every status change is confirmed first — the transitions are audit-logged, and going
// forward always needs someone to act deliberately even though a rollback now exists.
const CONFIRM_MESSAGE = {
  InternalReview: "Reviewer에게 검토를 요청합니다. 진행할까요?",
  Active:         "이 연구를 승인하여 Active 상태로 변경합니다. 진행할까요?",
  Draft:          "입력한 사유와 함께 CRA에게 반려합니다. 진행할까요?",
  Finalized:      "이 연구를 Finalize 합니다. 진행할까요?",
  Locked:         "이 연구를 Lock 합니다. 진행할까요?",
  Archived:       "이 연구를 Archive 합니다.\nArchived는 종료 상태로, 이후에는 이전 상태로 되돌릴 수 없습니다.\n진행할까요?",
};

// One step back, mirroring SFQ_ROLLBACK in services/Feasibility/index.js. The forward
// legs above are hardcoded per status too, so this file already keeps the graph in sync
// with the server by hand — the server is the one that enforces it.
//
// Archived is absent on purpose: it is the terminal state, and nothing leaves it. With no
// target, canRollback below is false there and the 되돌리기 button never renders.
const ROLLBACK_TARGET = {
  Locked:         "Finalized",
  Finalized:      "Active",
  Active:         "InternalReview",
  InternalReview: "Draft",
};

export default function StatusBar({ sfq, isOwner, isReviewer, isAdmin }) {
  const dispatch = useDispatch();
  // The working copy's saved questions, loaded by the same fetchSFQDetail that filled
  // `sfq` — the Questions half of the Submit for Review gate below.
  const questions = useSelector((s) => s.feasibility.sfqQuestions);
  const [busy, setBusy] = useState(false);
  // Send Back and rollback both collect a required reason, so they share one input:
  // null | "sendBack" | "rollback".
  const [reasonMode, setReasonMode] = useState(null);
  const [reason, setReason] = useState("");

  const status     = sfq?.status;
  const color      = STATUS_COLOR[status] || "#6c757d";
  const label      = STATUS_LABELS[status] || status;
  const versionNo  = sfq?.latestVersionNo;
  const isDirty    = !!sfq?.contentDirty;

  const trimmedReason = reason.trim();
  const reasonInvalid = !trimmedReason || trimmedReason.length > REASON_MAX;

  // Empty content is nothing to review, and the server refuses the transition too, so
  // the button stays disabled until both tabs hold something.
  const submitBlockers = status === "Draft" ? findSubmitBlockers(sfq, questions) : [];
  const canSubmit = submitBlockers.length === 0;

  const rollbackTo = ROLLBACK_TARGET[status];
  // InternalReview → Draft is the reviewer's Send Back, and the server keeps it that way
  // for them — reason, email to the owner, "반려 사유" notice. A second button for the
  // same edge would just be Send Back under the wrong label, so the reviewer only gets
  // Send Back here; an admin or the owner still rolls this leg back.
  const rollbackIsSendBack = isReviewer && status === "InternalReview";
  // The server also rejects a rollback while a publish request is open, since resolving
  // or cancelling it first keeps the reviewer's approval bar honest.
  const canRollback =
    (isAdmin || isOwner || isReviewer)
    && !!rollbackTo
    && !rollbackIsSendBack
    && !sfq?.publishPendingPM;

  function closeReason() {
    setReasonMode(null);
    setReason("");
  }

  async function doTransition(newStatus, reasonText, confirmOverride) {
    const message =
      confirmOverride ||
      CONFIRM_MESSAGE[newStatus] ||
      `상태를 ${STATUS_LABELS[newStatus] || newStatus}(으)로 변경할까요?`;
    if (!window.confirm(message)) return false;

    setBusy(true);
    try {
      const res = await dispatch(
        updateSFQStatus({ id: sfq.id, newStatus, reason: reasonText })
      ).unwrap();
      // Status changed, but the PM notification email may have failed to send.
      if (res?.emailWarning) alert(res.emailWarning);
      return true;
    } catch (e) {
      alert(e?.message || "Status update failed");
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function submitSendBack() {
    if (reasonInvalid) return;
    if (await doTransition("Draft", trimmedReason)) closeReason();
  }

  async function submitRollback() {
    if (reasonInvalid || !rollbackTo) return;
    const ok = await doTransition(
      rollbackTo,
      trimmedReason,
      `상태를 ${label} → ${STATUS_LABELS[rollbackTo] || rollbackTo}(으)로 되돌립니다. ` +
        `사유와 함께 감사 추적에 기록됩니다.` +
        // Only this leg mails anyone: it hands the reviewer back a study they had
        // already approved, so say so before it goes out. The reviewer rolling it back
        // themselves is not mailed (the server skips it), so neither is it promised.
        (rollbackTo === "InternalReview" && !isReviewer
          ? `\nReviewer에게 승인 철회 알림이 사유와 함께 발송됩니다.`
          : ``) +
        `\n진행할까요?`
    );
    if (!ok) return;
    closeReason();
    // A rollback also clears the Finalize/Lock/Archive timestamp and can change
    // server-derived fields the reducer does not track; refetch rather than guess.
    dispatch(fetchSFQDetail(sfq.id));
  }

  return (
    <Wrap>
      <span style={{ fontSize: 12, color: "#6c757d" }}>Status:</span>
      <StatusChip $color={color}>{label}</StatusChip>

      <Divider />

      <span style={{ fontSize: 12, color: "#6c757d" }}>Version:</span>
      {versionNo != null
        ? <VersionChip title="마지막으로 발행된 버전입니다.">V{versionNo}</VersionChip>
        : <span style={{ fontSize: 12, color: "#adb5bd" }}>발행 전</span>
      }
      {isDirty && <DirtyNote>• 미발행 변경사항 있음</DirtyNote>}

      {/* Owner: Draft → InternalReview, once there is content to review */}
      {isOwner && status === "Draft" && (
        <>
          <ActionBtn $filled disabled={busy || !canSubmit}
            title={canSubmit ? undefined : `${submitBlockers.join(", ")} 탭이 비어 있습니다.`}
            onClick={() => canSubmit && doTransition("InternalReview")}>
            Submit for Review
          </ActionBtn>
          {!canSubmit && (
            <BlockedNote>
              • 검토를 요청하려면 <b>{submitBlockers.join(" · ")}</b> 탭을 먼저 작성해 주세요.
            </BlockedNote>
          )}
        </>
      )}

      {status === "Draft" && sfq?.lastSendBackComment && (
        <SendBackNotice>
          <NoticeTitle>Reviewer 반려 사유</NoticeTitle>
          {sfq.lastSendBackComment}
        </SendBackNotice>
      )}

      {/* Why an already-approved study is back here. The server returns this only while
          the Active → InternalReview rollback is the newest status event, so the status
          check is belt-and-braces. */}
      {status === "InternalReview" && sfq?.lastRollbackComment && (
        <RollbackNotice>
          <NoticeTitle>Active에서 되돌려진 사유</NoticeTitle>
          {sfq.lastRollbackComment}
        </RollbackNotice>
      )}

      {/* Reviewer: InternalReview → Active or back to Draft (reason required) */}
      {isReviewer && status === "InternalReview" && (
        <>
          <ActionBtn $filled $color="#198754" disabled={busy || !!reasonMode}
            onClick={() => doTransition("Active")}>
            Approve
          </ActionBtn>
          <ActionBtn $color="#dc3545" disabled={busy || !!reasonMode}
            onClick={() => setReasonMode("sendBack")}>
            Send Back
          </ActionBtn>
        </>
      )}

      {/* Owner: Active → Finalized */}
      {isOwner && status === "Active" && (
        <ActionBtn $filled $color="#198754" disabled={busy}
          onClick={() => doTransition("Finalized")}>
          Finalize Study
        </ActionBtn>
      )}

      {/* Owner: Finalized → Locked */}
      {isOwner && status === "Finalized" && (
        <ActionBtn $color="#495057" disabled={busy}
          onClick={() => doTransition("Locked")}>
          Lock
        </ActionBtn>
      )}

      {/* Owner: Locked → Archived */}
      {isOwner && status === "Locked" && (
        <ActionBtn $color="#6c757d" disabled={busy}
          onClick={() => doTransition("Archived")}>
          Archive
        </ActionBtn>
      )}

      {/* Admin / Owner / Reviewer: one step back, reason required */}
      {canRollback && (
        <ActionBtn $color="#6c757d" disabled={busy || !!reasonMode}
          title={`${label} → ${STATUS_LABELS[rollbackTo] || rollbackTo}`}
          onClick={() => setReasonMode("rollback")}>
          ↩ 이전 상태로 되돌리기
        </ActionBtn>
      )}

      {/* Shared reason input for Send Back and rollback */}
      {reasonMode && (
        <ReasonBox $color={reasonMode === "rollback" ? "#6c757d" : "#dc3545"}>
          <ReasonLabel
            htmlFor="statusReason"
            $color={reasonMode === "rollback" ? "#6c757d" : "#dc3545"}
          >
            {reasonMode === "rollback"
              ? `되돌리는 사유 (필수) — ${label} → ${STATUS_LABELS[rollbackTo] || rollbackTo}, 감사 추적에 기록됩니다.`
              : "반려 사유 (필수) — CRA에게 전달되며 감사 추적에 기록됩니다."}
          </ReasonLabel>
          <ReasonInput
            id="statusReason"
            autoFocus
            value={reason}
            disabled={busy}
            placeholder={reasonMode === "rollback"
              ? "왜 이전 상태로 되돌리는지 적어주세요."
              : "어떤 부분을 수정해야 하는지 구체적으로 적어주세요."}
            onChange={(e) => setReason(e.target.value)}
          />
          <ReasonFoot>
            <ActionBtn
              $filled
              $color={reasonMode === "rollback" ? "#6c757d" : "#dc3545"}
              disabled={busy || reasonInvalid}
              onClick={reasonMode === "rollback" ? submitRollback : submitSendBack}
            >
              {busy ? "처리 중…" : reasonMode === "rollback" ? "되돌리기" : "반려하기"}
            </ActionBtn>
            <ActionBtn $color="#6c757d" disabled={busy} onClick={closeReason}>
              취소
            </ActionBtn>
            <CharCount $over={trimmedReason.length > REASON_MAX}>
              {trimmedReason.length} / {REASON_MAX}
            </CharCount>
          </ReasonFoot>
        </ReasonBox>
      )}
    </Wrap>
  );
}
